import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Popconfirm,
  Row,
  Select,
  Table,
  Space,
} from "antd";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import {
  createStockTransfers,
  getBranches,
  getProductStockByBranch,
} from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import QuantityStepper from "../../../components/QuantityStepper";
import { DeleteOutlined } from "@ant-design/icons";
import { handleApiError, showAppError } from "../../../utils/errorHandler";
import {
  clampQuantityToAvailableStock,
  isQuantityWithinAvailableStock,
  normalizePositiveQuantity,
} from "../../../utils/inventoryQuantity";

const DirectTransfer = ({ reloadCallback }) => {
  const { appConfig } = useContext(AppContext);
  const ADD_TRANSFER = "Add Transfer";
  const [modalTitle, setModalTitle] = useState(ADD_TRANSFER);
  const [modalVisible, setModalVisible] = useState(false);
  const [productsList, setProductsList] = useState([]);
  const [adjustmentData, setAdjustmentData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [loadingBTN, setLoadingBTN] = useState(false);
  const [branchId, setBranchId] = useState(0);
  const [branchToId, setBranchToId] = useState(0);
  const { t } = useTranslation();
  const [directTransferForm] = Form.useForm();

  useEffect(() => {
    branchId &&
      getProductStockByBranch(branchId)
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            return;
          }

          const products = response?.data?.data?.products;
          setProductsList(Array.isArray(products) ? products : []);
        })
        .catch((error) => handleApiError(error, t));
  }, [branchId, t]);

  useEffect(() => {
    setIsDataLoading(true);
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        const branches = response?.data?.data?.branches;
        setBranchesData(Array.isArray(branches) ? branches : []);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  const adjustmentReducer = (product, action, quantity) => {
    let updatedAdjustment = JSON.parse(JSON.stringify(adjustmentData));
    let productId = product.product.product_id;
    let indexFound = updatedAdjustment.findIndex(
      (product) => product.product.product_id === productId,
    );
    if (action === "add") {
      if (indexFound === -1) {
        product.qty = 1;
        updatedAdjustment.push(product);
      } else {
        showAppError(t("product_already_selected"));
      }
    } else if (action === "inc") {
      const item = updatedAdjustment[indexFound];
      item.qty = clampQuantityToAvailableStock(item.qty + 1, item.quantity);
    } else if (action === "dec") {
      updatedAdjustment[indexFound].qty = normalizePositiveQuantity(
        updatedAdjustment[indexFound].qty - 1,
      );
    } else if (action === "remove") {
      updatedAdjustment.splice(indexFound, 1);
    } else if (action === "directInput") {
      const item = updatedAdjustment[indexFound];
      item.qty = clampQuantityToAvailableStock(quantity, item.quantity);
    }
    setAdjustmentData(updatedAdjustment);
  };

  const showAddModal = () => {
    setModalTitle(t("direct_transfer"));
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    directTransferForm.resetFields();
    setAdjustmentData([]);
    setProductsList([]);
    setBranchId(0);
    setBranchToId(0);
  };

  const handleAddTransfer = () => {
    directTransferForm
      .validateFields()
      .then((values) => {
        const hasInvalidQuantity = adjustmentData.some(
          (item) => !isQuantityWithinAvailableStock(item.qty, item.quantity),
        );

        if (hasInvalidQuantity) {
          showAppError(t("insufficient_quantity"));
          return;
        }

        setLoadingBTN(true);
        let transactionObj = {
          branch_from_id: values.branch_from_id,
          branch_to_id: values.branch_to_id,
        };
        transactionObj.stocks = adjustmentData.map((item) => ({
          product_id: item.product.product_id
            ? item.product.product_id
            : item.product.id,
          requested_quantity: item.qty,
        }));
        createStockTransfers(transactionObj)
          .then((response) => {
            let data = response.data || response.response.data;
            if (data.success === true) {
              NotificationWithIcon(
                "success",
                t("transfer_added"),
                t("transfer_added_successfully"),
              );
              reloadCallback();
              setModalVisible(false);
              directTransferForm.resetFields();
              setAdjustmentData([]);
              setProductsList([]);
              setBranchId(0);
              setBranchToId(0);
            } else {
              handleApiError(response, t);
            }
          })
          .catch((error) => handleApiError(error, t))
          .finally(() => {
            setLoadingBTN(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleProductSelect = (value) => {
    const product = productsList.find((item) => item?.product?.id === value);
    if (!product?.product) return;

    product.product.product_id = product.product.id;
    adjustmentReducer(product, "add");
    directTransferForm.setFieldsValue({
      product: "",
    });
  };

  const productTableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("image"),
      width: 85,
      render: (record) => {
        return (
          <>
            <img
              alt={record.product.name_en}
              src={
                record.product.image
                  ? record.product.image
                  : "/images/image-not-found.png"
              }
              className="app-table-thumbnail"
            />
          </>
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          <>
            {appConfig.direction === "ltr"
              ? record.product.name_en
              : record.product.name}
          </>
        );
      },
    },
    {
      title: t("available_stock"),
      align: "center",
      dataIndex: "quantity",
    },
    {
      title: t("quantity"),
      align: "center",
      render: (record) => {
        return (
          <>
            <QuantityStepper
              value={record.qty}
              min={1}
              max={record.quantity}
              showInput
              decreaseDisabled={record.qty <= 1}
              increaseDisabled={record.quantity <= record.qty}
              onDecrease={() => adjustmentReducer(record, "dec")}
              onIncrease={() => adjustmentReducer(record, "inc")}
              onChange={(quantity) =>
                adjustmentReducer(record, "directInput", quantity)
              }
            />
          </>
        );
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => {
        return (
          <Space>
            <Popconfirm
              title={t("warning_remove")}
              onConfirm={() => adjustmentReducer(record, "remove")}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Button type="primary" onClick={showAddModal}>
        {t("direct_transfer")}
      </Button>
      <Modal
        open={modalVisible}
        title={modalTitle}
        onOk={handleAddTransfer}
        onCancel={handleCancel}
        width={1000}
        footer={[
          <Button
            type="default"
            key="back"
            loading={loadingBTN}
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            loading={loadingBTN}
            type="primary"
            onClick={handleAddTransfer}
          >
            {t("add")}
          </Button>,
        ]}
      >
        <Form
          name="Direct-Stock-Transer-form"
          layout="vertical"
          onFinish={handleAddTransfer}
          className="app-form-full"
          form={directTransferForm}
        >
          <Row gutter={[20, 20]}>
            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              xxl={8}
            >
              <Form.Item
                name="branch_from_id"
                label={t("branch_from")}
                rules={[
                  {
                    required: true,
                    message: t("validate_branch_required"),
                  },
                ]}
                hasFeedback
              >
                <Select
                  showSearch
                  allowClear
                  onChange={(e) => {
                    setBranchId(e || 0);
                    setAdjustmentData([]);
                    setProductsList([]);
                  }}
                  options={branchesData
                    .filter((branch) => branch?.id !== branchToId)
                    .map((branch) => {
                      return {
                        label: branch?.name || "-",
                        value: branch.id,
                      };
                    })}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              xxl={8}
            >
              <Form.Item
                name="branch_to_id"
                label={t("branch_to")}
                rules={[
                  {
                    required: true,
                    message: t("validate_branch_required"),
                  },
                ]}
                hasFeedback
              >
                <Select
                  showSearch
                  allowClear
                  onChange={(value) => setBranchToId(value || 0)}
                  options={branchesData
                    .filter((branch) => branch?.id !== branchId)
                    .map((branch) => {
                      return {
                        label: branch?.name || "-",
                        value: branch.id,
                      };
                    })}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            {branchId ? (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="product"
                  label={t("product_add")}
                  rules={[
                    adjustmentData.length < 1 && {
                      required: true,
                      message: t("validate_product_name_required"),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    options={productsList
                      .filter((product) => {
                        if (product.quantity > 0) {
                          return product;
                        } else return null;
                      })
                      .map((product) => {
                        return {
                          label:
                            appConfig.direction === "ltr"
                              ? `${product.product.name_en} (${product.quantity})`
                              : `${product.product.name} (${product.quantity})`,
                          value: product.product.id,
                        };
                      })}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    onChange={handleProductSelect}
                  />
                </Form.Item>
              </Col>
            ) : (
              ""
            )}
            {adjustmentData.length > 0 && (
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={24}
              >
                <Table
                  columns={productTableColumns}
                  dataSource={adjustmentData}
                  loading={isDataLoading}
                  rowKey="id"
                  pagination={false}
                />
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default DirectTransfer;
